import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import BrandLogo from '../components/BrandLogo';

const BRAND = 'Zaya Life';
const TAGLINE = 'Abu Dhabi Surrogacy Agency';

function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true);
          obs.unobserve(el);
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function FadeIn({ children, delay = 0, style = {} }) {
  const [ref, inView] = useInView(0.08);
  return (
    <div
      ref={ref}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(28px)',
        transition: `opacity 0.7s cubic-bezier(0.22,1,0.36,1) ${delay}s, transform 0.7s cubic-bezier(0.22,1,0.36,1) ${delay}s`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

const C = {
  bg: '#FDFBF7',
  bgWarm: '#F8F4ED',
  bgSoft: '#F2EDE4',
  text: '#2C2520',
  textMed: '#5C5347',
  textLight: '#8C8278',
  textFaint: '#B0A89E',
  sage: '#7A9B6D',
  sageDark: '#5C7D50',
  sageLight: '#E8F0E4',
  sageBg: '#F3F7F1',
  gold: '#C4A265',
  goldLight: '#F5EFE0',
  border: '#E8E2D8',
  borderLight: '#F0EBE3',
};
const font = {
  display: "'Merriweather', Georgia, serif",
  body: "'Inter', 'Helvetica Neue', sans-serif",
};

const Check = ({ filled }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    style={{ flexShrink: 0 }}
  >
    <circle
      cx="8"
      cy="8"
      r="7"
      fill={filled ? C.sageLight : 'none'}
      stroke={filled ? C.sage : C.border}
      strokeWidth="1.5"
    />
    <path
      d="M5.5 8L7.2 9.7L10.5 6.3"
      stroke={C.sage}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ── NAV ── */
function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);
  return (
    <nav
      style={{
        position: 'fixed',
        top: 'var(--banner-h, 0px)',
        left: 0,
        right: 0,
        zIndex: 100,
        background: scrolled ? 'rgba(253,251,247,0.95)' : 'transparent',
        backdropFilter: scrolled ? 'blur(16px)' : 'none',
        borderBottom: scrolled
          ? `1px solid ${C.borderLight}`
          : '1px solid transparent',
        transition: 'all 0.4s ease',
        padding: '0 clamp(20px, 5vw, 72px)',
      }}
    >
      <div
        style={{
          maxWidth: 1140,
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: scrolled ? 110 : 132,
          transition: 'height 0.4s ease',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Link to="/" style={{ textDecoration: 'none', lineHeight: 0 }}>
            <BrandLogo
              iconSize={114}
              textSize={24}
              textColor={C.sageDark}
            />
          </Link>
          <span
            className="nav-tagline"
            style={{
              fontSize: 10,
              color: C.textFaint,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              fontFamily: font.body,
            }}
          >
            {TAGLINE}
          </span>
        </div>
        <div className="nav-links" style={{ display: 'flex', alignItems: 'center', gap: 36 }}>
          {[
            { name: 'Home', path: '/' },
            { name: 'For Parents', path: '/parents' },
            { name: 'Programs', path: '/programs' }
          ].map((item) => (
            <Link
              key={item.name}
              to={item.path}
              style={{
                color: item.name === 'Programs' ? C.text : C.textMed,
                fontWeight: item.name === 'Programs' ? 600 : 400,
                fontSize: 14,
                textDecoration: 'none',
                fontFamily: font.body,
                transition: 'color 0.3s',
              }}
              onMouseEnter={(e) => (e.target.style.color = C.text)}
              onMouseLeave={(e) => {
                if (item.name !== 'Programs') e.target.style.color = C.textMed;
              }}
            >
              {item.name}
            </Link>
          ))}
          <a
            href="/#consultation"
            className="nav-cta"
            style={{
              color: '#fff',
              background: C.sage,
              padding: '10px 26px',
              fontSize: 13,
              fontFamily: font.body,
              fontWeight: 600,
              textDecoration: 'none',
              borderRadius: 6,
              transition: 'all 0.3s',
            }}
            onMouseEnter={(e) => (e.target.style.background = C.sageDark)}
            onMouseLeave={(e) => (e.target.style.background = C.sage)}
          >
            Register Your Interest
          </a>
        </div>
        <button
          className="nav-mobile-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          style={{ display: "none" }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={C.text} strokeWidth="2" strokeLinecap="round">
            {mobileMenuOpen ? (
              <>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </>
            ) : (
              <>
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </>
            )}
          </svg>
        </button>
      </div>
      {mobileMenuOpen && (
        <div className="nav-mobile-menu" style={{
          position: "absolute", top: "100%", left: 0, right: 0,
          background: "rgba(253,251,247,0.98)", backdropFilter: "blur(16px)",
          borderBottom: `1px solid ${C.borderLight}`, padding: "20px",
        }}>
          {[
            { name: 'Home', path: '/' },
            { name: 'For Parents', path: '/parents' },
            { name: 'Programs', path: '/programs' }
          ].map(item => (
            <Link key={item.name} to={item.path} style={{
              display: "block", color: C.textMed, fontSize: 16, textDecoration: "none",
              fontFamily: font.body, fontWeight: 400, padding: "12px 0",
              borderBottom: `1px solid ${C.borderLight}`,
            }}
              onClick={() => setMobileMenuOpen(false)}
            >{item.name}</Link>
          ))}
          <a href="/#consultation" style={{
            display: "block", color: "#fff", background: C.sage, padding: "14px 26px",
            fontSize: 15, fontFamily: font.body, fontWeight: 600, textAlign: "center",
            textDecoration: "none", borderRadius: 6, marginTop: "16px",
          }}
            onClick={() => setMobileMenuOpen(false)}
          >Register Your Interest</a>
        </div>
      )}
    </nav>
  );
}

/* ── PAGE HERO ── */
function PageHero() {
  return (
    <section
      className="hero-section"
      style={{
        padding: '140px clamp(20px,5vw,72px) 80px',
        background: `linear-gradient(170deg, ${C.bg}, ${C.bgWarm})`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          bottom: -80,
          left: -60,
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${C.goldLight} 0%, transparent 70%)`,
          opacity: 0.35,
        }}
      />
      <div className="grid-hero" style={{ maxWidth: 1140, margin: '0 auto', position: 'relative', display: 'grid', gridTemplateColumns: '1fr 380px', gap: 64, alignItems: 'center' }}>
        <div>
          <FadeIn>
            <div
              style={{
                fontSize: 12,
                color: C.sage,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                marginBottom: 16,
                fontFamily: font.body,
                fontWeight: 600,
              }}
            >
              Programs
            </div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <h1
              className="responsive-h1 programs-hero-title"
              style={{
                fontFamily: font.display,
                fontSize: 'clamp(34px, 4.5vw, 56px)',
                color: C.text,
                fontWeight: 600,
                lineHeight: 1.12,
                maxWidth: 700,
                margin: '0 0 24px',
                letterSpacing: '-0.02em',
              }}
            >
              Two programs. One accredited facility.
            </h1>
          </FadeIn>
          <FadeIn delay={0.2}>
            <p
              style={{
                fontSize: 18,
                color: C.textMed,
                maxWidth: 600,
                lineHeight: 1.75,
                fontFamily: font.body,
                margin: 0,
              }}
            >
              Every surrogacy in our program takes place at the same
              DoH-accredited IVF center in Abu Dhabi. What differs is how much of
              the coordination you handle versus what we manage for you.
            </p>
          </FadeIn>
        </div>

        <FadeIn delay={0.25}>
          <div style={{ position: 'relative' }}>
            <div style={{
              position: 'absolute', top: -12, right: -12, width: '100%', height: '100%',
              borderRadius: 24, border: `2px solid ${C.sageLight}`, zIndex: 0,
            }} />
            <div style={{
              position: 'relative', zIndex: 1, borderRadius: 20, overflow: 'hidden',
              boxShadow: '0 8px 48px rgba(44,37,32,0.1)',
              aspectRatio: '4/3',
            }}>
              <img
                src="https://images.unsplash.com/photo-1666214280557-f1b5022eb634?w=960&h=720&fit=crop&q=80"
                alt="Parents consulting with doctor"
                style={{
                  width: '100%', height: '100%', objectFit: 'cover', display: 'block',
                }}
              />
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0, height: '30%',
                background: `linear-gradient(to top, ${C.bgWarm}44, transparent)`,
              }} />
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ── TIER COMPARISON ── */
function TierComparison() {
  const features = [
    { name: 'Global carrier sourcing and database access', t1: true, t2: true },
    {
      name: 'Medical and psychological pre-screening (home country)',
      t1: true,
      t2: true,
    },
    {
      name: 'Carrier visa coordination and travel to Abu Dhabi',
      t1: true,
      t2: true,
    },
    { name: 'IVF center introduction and onboarding', t1: true, t2: true },
    { name: 'Tripartite legal contract coordination', t1: true, t2: true },
    {
      name: 'Dedicated coordinator — single point of contact',
      t1: false,
      t2: true,
    },
    { name: 'Carrier housing management in Abu Dhabi', t1: false, t2: true },
    {
      name: 'Ongoing carrier welfare and care management',
      t1: false,
      t2: true,
    },
    { name: 'Prenatal appointment coordination', t1: false, t2: true },
    { name: 'Birth logistics and hospital coordination', t1: false, t2: true },
    { name: 'Post-birth carrier recovery and departure', t1: false, t2: true },
    {
      name: 'Post-birth documentation support for parents',
      t1: false,
      t2: true,
    },
  ];

  return (
    <section
      className="section-padding-lg"
      style={{ padding: '100px clamp(20px,5vw,72px)', background: '#fff' }}
    >
      <div style={{ maxWidth: 1140, margin: '0 auto' }}>
        <FadeIn>
          <div
            style={{
              fontSize: 12,
              color: C.sage,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              marginBottom: 14,
              fontFamily: font.body,
              fontWeight: 600,
            }}
          >
            Side-by-side comparison
          </div>
        </FadeIn>
        <FadeIn delay={0.08}>
          <h2
            className="responsive-h2"
            style={{
              fontFamily: font.display,
              fontSize: 'clamp(28px, 3.5vw, 42px)',
              color: C.text,
              fontWeight: 600,
              lineHeight: 1.2,
              margin: '0 0 48px',
            }}
          >
            What's included in each program
          </h2>
        </FadeIn>

        <FadeIn delay={0.15}>
          <div
            className="comparison-table"
            style={{
              background: C.bg,
              borderRadius: 16,
              border: `1px solid ${C.borderLight}`,
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div
              className="comparison-grid comparison-grid-header"
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 180px 180px',
                borderBottom: `2px solid ${C.border}`,
              }}
            >
              <div className="comparison-feature-spacer" style={{ padding: '24px 32px' }} />
              <div
                className="comparison-tier-cell"
                style={{
                  padding: '24px 20px',
                  textAlign: 'center',
                  borderLeft: `1px solid ${C.borderLight}`,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    color: C.textFaint,
                    fontFamily: font.body,
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    marginBottom: 4,
                  }}
                >
                  Tier 1
                </div>
                <div
                  style={{
                    fontSize: 20,
                    color: C.text,
                    fontFamily: font.display,
                    fontWeight: 600,
                  }}
                >
                  Matching
                </div>
              </div>
              <div
                className="comparison-tier-cell"
                style={{
                  padding: '24px 20px',
                  textAlign: 'center',
                  background: C.sageBg,
                  borderLeft: `1px solid ${C.sageLight}`,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    color: C.textFaint,
                    fontFamily: font.body,
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    marginBottom: 4,
                  }}
                >
                  Tier 2
                </div>
                <div
                  style={{
                    fontSize: 20,
                    color: C.text,
                    fontFamily: font.display,
                    fontWeight: 600,
                  }}
                >
                  White Glove
                </div>
              </div>
            </div>

            {/* Rows */}
            {features.map((f, i) => (
              <div
                className="comparison-grid-row"
                key={i}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 180px 180px',
                  borderBottom:
                    i < features.length - 1
                      ? `1px solid ${C.borderLight}`
                      : 'none',
                }}
              >
                <div
                  className="comparison-feature-cell"
                  style={{
                    padding: '14px 32px',
                    fontSize: 14,
                    color: C.textMed,
                    fontFamily: font.body,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {f.name}
                </div>
                <div
                  className="comparison-tier-cell"
                  style={{
                    padding: '14px 20px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderLeft: `1px solid ${C.borderLight}`,
                  }}
                >
                  {f.t1 ? (
                    <Check />
                  ) : (
                    <span style={{ color: C.textFaint, fontSize: 16 }}>—</span>
                  )}
                </div>
                <div
                  className="comparison-tier-cell"
                  style={{
                    padding: '14px 20px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    background:
                      i % 2 === 0 ? C.sageBg : 'rgba(243,247,241,0.5)',
                    borderLeft: `1px solid ${C.sageLight}`,
                  }}
                >
                  {f.t2 ? (
                    <Check filled />
                  ) : (
                    <span style={{ color: C.textFaint, fontSize: 16 }}>—</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </FadeIn>

        <FadeIn delay={0.25}>
          <p
            style={{
              fontSize: 13,
              color: C.textFaint,
              textAlign: 'center',
              marginTop: 24,
              fontFamily: font.body,
            }}
          >
            Both tiers: medical care is contracted directly between you and the IVF center,
            while we coordinate matching, screening, and all logistics.
          </p>
        </FadeIn>
      </div>
    </section>
  );
}

/* ── TIER DETAIL CARDS ── */
function TierDetails() {
  return (
    <section
      className="section-padding-lg"
      style={{ padding: '100px clamp(20px,5vw,72px)', background: C.bgWarm }}
    >
      <div style={{ maxWidth: 1140, margin: '0 auto' }}>
        <FadeIn>
          <h2
            className="responsive-h2"
            style={{
              fontFamily: font.display,
              fontSize: 'clamp(26px, 3vw, 38px)',
              color: C.text,
              fontWeight: 600,
              lineHeight: 1.25,
              margin: '0 0 48px',
            }}
          >
            Which program is right for you?
          </h2>
        </FadeIn>

        <div
          className="grid-2-cols"
          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}
        >
          <FadeIn delay={0.1}>
            <div
              style={{
                padding: 40,
                background: '#fff',
                borderRadius: 16,
                border: `1px solid ${C.borderLight}`,
                height: '100%',
              }}
            >
              <div
                style={{
                  display: 'inline-block',
                  padding: '4px 14px',
                  background: C.bg,
                  borderRadius: 100,
                  fontSize: 12,
                  color: C.textLight,
                  fontFamily: font.body,
                  fontWeight: 600,
                  marginBottom: 20,
                }}
              >
                Tier 1
              </div>
              <h3
                style={{
                  fontFamily: font.display,
                  fontSize: 26,
                  color: C.text,
                  fontWeight: 600,
                  margin: '0 0 6px',
                }}
              >
                Matching
              </h3>
              <p
                style={{
                  fontSize: 15,
                  color: C.textMed,
                  lineHeight: 1.7,
                  fontFamily: font.body,
                  margin: '0 0 24px',
                }}
              >
                We find and screen your carrier, handle visa and travel
                logistics, and introduce you to the IVF center. From that point,
                you coordinate directly with the clinic. This tier covers Steps
                1–7 of our 13-step process.
              </p>
              <div
                style={{
                  fontSize: 14,
                  color: C.text,
                  fontFamily: font.body,
                  fontWeight: 600,
                  marginBottom: 12,
                }}
              >
                Best for:
              </div>
              {[
                'International parents familiar with managing medical processes',
                'Couples who want to be hands-on through pregnancy',
                'US waitlist overflow looking for a matched carrier quickly',
              ].map((item, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    gap: 10,
                    marginBottom: 10,
                    alignItems: 'start',
                  }}
                >
                  <div
                    style={{
                      width: 5,
                      height: 5,
                      borderRadius: '50%',
                      background: C.textFaint,
                      marginTop: 7,
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontSize: 14,
                      color: C.textMed,
                      lineHeight: 1.55,
                      fontFamily: font.body,
                    }}
                  >
                    {item}
                  </span>
                </div>
              ))}
              <div
                style={{
                  marginTop: 28,
                  padding: 20,
                  background: C.bg,
                  borderRadius: 10,
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    color: C.textLight,
                    fontFamily: font.body,
                    fontWeight: 500,
                    marginBottom: 4,
                  }}
                >
                  Your involvement after matching:
                </div>
                <div
                  style={{
                    fontSize: 14,
                    color: C.text,
                    fontFamily: font.body,
                    fontWeight: 500,
                  }}
                >
                  High — you manage carrier housing, appointments, and birth
                  logistics directly with the IVF center.
                </div>
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={0.2}>
            <div
              style={{
                padding: 40,
                background: '#fff',
                borderRadius: 16,
                border: `2px solid ${C.sage}`,
                height: '100%',
                position: 'relative',
                boxShadow: `0 8px 40px rgba(122,155,109,0.1)`,
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: -1,
                  right: 28,
                  padding: '6px 16px',
                  background: C.sage,
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 600,
                  fontFamily: font.body,
                  borderRadius: '0 0 8px 8px',
                }}
              >
                Recommended
              </div>
              <div
                style={{
                  display: 'inline-block',
                  padding: '4px 14px',
                  background: C.sageBg,
                  borderRadius: 100,
                  fontSize: 12,
                  color: C.sageDark,
                  fontFamily: font.body,
                  fontWeight: 600,
                  marginBottom: 20,
                }}
              >
                Tier 2
              </div>
              <h3
                style={{
                  fontFamily: font.display,
                  fontSize: 26,
                  color: C.text,
                  fontWeight: 600,
                  margin: '0 0 6px',
                }}
              >
                White Glove
              </h3>
              <p
                style={{
                  fontSize: 15,
                  color: C.textMed,
                  lineHeight: 1.7,
                  fontFamily: font.body,
                  margin: '0 0 24px',
                }}
              >
                Everything in Matching plus full concierge coordination from
                embryo transfer through birth and beyond. We manage carrier
                housing, welfare, appointments, birth logistics, and post-birth
                support. You show up for the moments that matter. This tier
                covers all 13 steps.
              </p>
              <div
                style={{
                  fontSize: 14,
                  color: C.text,
                  fontFamily: font.body,
                  fontWeight: 600,
                  marginBottom: 12,
                }}
              >
                Best for:
              </div>
              {[
                'GCC residents and Emiratis who expect a premium, managed experience',
                'Parents who want a dedicated coordinator and single point of contact',
                'Couples who prefer to focus on the personal milestones, not the paperwork',
              ].map((item, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    gap: 10,
                    marginBottom: 10,
                    alignItems: 'start',
                  }}
                >
                  <div
                    style={{
                      width: 5,
                      height: 5,
                      borderRadius: '50%',
                      background: C.sage,
                      marginTop: 7,
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontSize: 14,
                      color: C.textMed,
                      lineHeight: 1.55,
                      fontFamily: font.body,
                    }}
                  >
                    {item}
                  </span>
                </div>
              ))}
              <div
                style={{
                  marginTop: 28,
                  padding: 20,
                  background: C.sageBg,
                  borderRadius: 10,
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    color: C.sageDark,
                    fontFamily: font.body,
                    fontWeight: 500,
                    marginBottom: 4,
                  }}
                >
                  Your involvement after matching:
                </div>
                <div
                  style={{
                    fontSize: 14,
                    color: C.text,
                    fontFamily: font.body,
                    fontWeight: 500,
                  }}
                >
                  Minimal — attend 2–3 key appointments in Abu Dhabi. We handle
                  everything in between.
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
}


/* ── FULL PROCESS ── */
function FullProcess() {
  const steps = [
    {
      num: '01',
      title: 'Recruitment',
      loc: 'Home country',
      desc: 'Local recruiters identify candidates and pre-screen against DoH criteria.',
      tier: 'Both',
    },
    {
      num: '02',
      title: 'Medical & psychological screening',
      loc: 'Home country',
      desc: 'Full medical evaluation at accredited clinic. Psychological evaluation. Background check.',
      tier: 'Both',
    },
    {
      num: '03',
      title: 'Profile creation',
      loc: 'Home country',
      desc: 'Professional profile created with photos, video interview, bio, and medical summary.',
      tier: 'Both',
    },
    {
      num: '04',
      title: 'IP eligibility',
      loc: 'Abu Dhabi',
      desc: 'IVF center confirms your eligibility. DoH Medical Committee reviews and approves.',
      tier: 'Both',
    },
    {
      num: '05',
      title: 'Matching',
      loc: 'Remote',
      desc: 'You browse our carrier database, select a carrier, and we arrange a video introduction.',
      tier: 'Both',
    },
    {
      num: '06',
      title: 'Final screening',
      loc: 'Home country',
      desc: 'Comprehensive final medical check per DoH standards. Visa process initiated.',
      tier: 'Both',
    },
    {
      num: '07',
      title: 'Visa & travel',
      loc: 'Home country → Abu Dhabi',
      desc: "Agency arranges carrier's employment visa. Carrier travels to the UAE.",
      tier: 'Both',
    },
    {
      num: '08',
      title: 'UAE medical onboarding',
      loc: 'Abu Dhabi',
      desc: 'Full screening at IVF center. Endometrial preparation (20–25 days).',
      tier: 'White Glove',
    },
    {
      num: '09',
      title: 'Legal agreement',
      loc: 'Abu Dhabi',
      desc: 'Notarized tripartite contract signed. Parental rights and liability defined.',
      tier: 'White Glove',
    },
    {
      num: '10',
      title: 'Embryo transfer',
      loc: 'Abu Dhabi',
      desc: 'Single PGT-A tested embryo transfer. Pregnancy confirmation ~12 days post-transfer.',
      tier: 'White Glove',
    },
    {
      num: '11',
      title: 'Pregnancy care',
      loc: 'Abu Dhabi',
      desc: 'Full prenatal care at IVF center. Regular updates. ~9 months.',
      tier: 'White Glove',
    },
    {
      num: '12',
      title: 'Birth',
      loc: 'Abu Dhabi',
      desc: 'Delivery at DoH-licensed hospital. Birth certificate issued in your names.',
      tier: 'White Glove',
    },
    {
      num: '13',
      title: 'Post-birth',
      loc: 'Abu Dhabi',
      desc: 'Carrier recovery and return. IP documentation, nationality, passport processing.',
      tier: 'White Glove',
    },
  ];

  return (
    <section
      className="section-padding-lg"
      style={{ padding: '100px clamp(20px,5vw,72px)', background: '#fff' }}
    >
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <FadeIn>
          <div
            style={{
              fontSize: 12,
              color: C.sage,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              marginBottom: 14,
              fontFamily: font.body,
              fontWeight: 600,
            }}
          >
            Full 13-step process
          </div>
        </FadeIn>
        <FadeIn delay={0.08}>
          <h2
            className="responsive-h2"
            style={{
              fontFamily: font.display,
              fontSize: 'clamp(26px, 3vw, 38px)',
              color: C.text,
              fontWeight: 600,
              lineHeight: 1.25,
              margin: '0 0 16px',
            }}
          >
            Every step, from recruitment to birth certificate
          </h2>
        </FadeIn>
        <FadeIn delay={0.14}>
          <p
            className="responsive-description"
            style={{
              fontSize: 16,
              color: C.textLight,
              maxWidth: 560,
              margin: '0 0 48px',
              fontFamily: font.body,
              lineHeight: 1.65,
            }}
          >
            Matching tier covers Steps 1–7. White Glove covers all 13.
          </p>
        </FadeIn>

        {steps.map((step, i) => (
          <FadeIn key={i} delay={Math.min(i * 0.04, 0.4)}>
            <div
              className="step-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: '48px 1fr 140px',
                gap: 20,
                padding: '20px 0',
                borderTop: `1px solid ${C.borderLight}`,
                opacity: step.tier === 'White Glove' ? 1 : 1,
              }}
            >
              <div
                style={{
                  fontFamily: font.display,
                  fontSize: 22,
                  color: C.sage,
                  fontWeight: 600,
                  opacity: 0.5,
                }}
              >
                {step.num}
              </div>
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginBottom: 4,
                  }}
                >
                  <span
                    style={{
                      fontSize: 15,
                      color: C.text,
                      fontFamily: font.body,
                      fontWeight: 600,
                    }}
                  >
                    {step.title}
                  </span>
                  {step.tier === 'White Glove' && (
                    <span
                      style={{
                        fontSize: 10,
                        color: C.sageDark,
                        background: C.sageBg,
                        padding: '2px 8px',
                        borderRadius: 100,
                        fontFamily: font.body,
                        fontWeight: 600,
                      }}
                    >
                      White Glove only
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontSize: 14,
                    color: C.textMed,
                    lineHeight: 1.6,
                    fontFamily: font.body,
                  }}
                >
                  {step.desc}
                </div>
              </div>
              <div
                className="step-location-full"
                style={{
                  fontSize: 12,
                  color: C.textFaint,
                  fontFamily: font.body,
                  textAlign: 'right',
                  paddingTop: 3,
                }}
              >
                {step.loc}
              </div>
            </div>
          </FadeIn>
        ))}
      </div>
    </section>
  );
}

/* ── CTA ── */
function CTASection() {
  return (
    <section
      id="consultation"
      className="section-padding"
      style={{ padding: '80px clamp(20px,5vw,72px)', background: C.bgSoft }}
    >
      <div style={{ maxWidth: 640, margin: '0 auto', textAlign: 'center' }}>
        <FadeIn>
          <h2
            className="responsive-h2"
            style={{
              fontFamily: font.display,
              fontSize: 'clamp(26px, 3vw, 36px)',
              color: C.text,
              fontWeight: 600,
              lineHeight: 1.3,
              margin: '0 0 16px',
            }}
          >
            Ready to understand which program fits your situation?
          </h2>
        </FadeIn>
        <FadeIn delay={0.08}>
          <p
            className="responsive-description"
            style={{
              fontSize: 16,
              color: C.textMed,
              lineHeight: 1.7,
              fontFamily: font.body,
              margin: '0 0 32px',
            }}
          >
            A private consultation where we walk through your specific
            circumstances, explain both program options in detail, and help you decide
            between Matching and White Glove — or whether Abu Dhabi is the right
            destination at all.
          </p>
        </FadeIn>
        <FadeIn delay={0.16}>
          <div
            className="btn-group"
            style={{
              display: 'flex',
              gap: 14,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <a
              href="/#consultation"
              style={{
                padding: '14px 32px',
                background: C.sage,
                color: '#fff',
                textDecoration: 'none',
                fontSize: 15,
                fontFamily: font.body,
                fontWeight: 600,
                borderRadius: 6,
                transition: 'all 0.3s',
              }}
              onMouseEnter={(e) => (e.target.style.background = C.sageDark)}
              onMouseLeave={(e) => (e.target.style.background = C.sage)}
            >
              Register Your Interest
            </a>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ── FOOTER ── */
function Footer() {
  return (
    <footer
      style={{ padding: '56px clamp(20px,5vw,72px) 36px', background: C.text }}
    >
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'start',
            marginBottom: 44,
          }}
        >
          <div>
            <div
              style={{
                fontFamily: font.display,
                fontSize: 20,
                color: '#fff',
                fontWeight: 600,
                marginBottom: 8,
              }}
            >
              {BRAND}
            </div>
            <div
              style={{
                fontSize: 13,
                color: 'rgba(255,255,255,0.4)',
                fontFamily: font.body,
                lineHeight: 1.6,
                maxWidth: 260,
              }}
            >
              Surrogacy coordination agency.
              <br />
              Abu Dhabi, United Arab Emirates.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 48 }}>
            <div>
              <div
                style={{
                  fontSize: 11,
                  color: 'rgba(255,255,255,0.3)',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  marginBottom: 14,
                  fontFamily: font.body,
                  fontWeight: 600,
                }}
              >
                Navigate
              </div>
              {[
                { name: 'Home', path: '/' },
                { name: 'For Parents', path: '/parents' },
                { name: 'Programs', path: '/programs' },
                { name: 'FAQ', path: '#' }
              ].map((item) => (
                item.path === '#' ? (
                  <button
                    key={item.name}
                    type="button"
                    style={{
                      display: 'block',
                      color: 'rgba(255,255,255,0.55)',
                      fontSize: 14,
                      textDecoration: 'none',
                      marginBottom: 10,
                      fontFamily: font.body,
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: 'default',
                    }}
                  >
                    {item.name}
                  </button>
                ) : (
                  <Link
                    key={item.name}
                    to={item.path}
                    style={{
                      display: 'block',
                      color: 'rgba(255,255,255,0.55)',
                      fontSize: 14,
                      textDecoration: 'none',
                      marginBottom: 10,
                      fontFamily: font.body,
                    }}
                  >
                    {item.name}
                  </Link>
                )
              ))}
            </div>
            <div>
              <div
                style={{
                  fontSize: 11,
                  color: 'rgba(255,255,255,0.3)',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  marginBottom: 14,
                  fontFamily: font.body,
                  fontWeight: 600,
                }}
              >
                Contact
              </div>
              <div
                style={{
                  fontSize: 14,
                  color: 'rgba(255,255,255,0.55)',
                  fontFamily: font.body,
                }}
              >
                info@zayalife.ae
              </div>
            </div>
          </div>
        </div>
        <div
          style={{
            borderTop: '1px solid rgba(255,255,255,0.08)',
            paddingTop: 20,
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <div
            style={{
              fontSize: 12,
              color: 'rgba(255,255,255,0.25)',
              fontFamily: font.body,
            }}
          >
            © 2026 {BRAND}. All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function App() {
  useEffect(() => {
    // Handle hash navigation for smooth scrolling
    if (window.location.hash) {
      setTimeout(() => {
        const id = window.location.hash.substring(1);
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);

  return (
    <div style={{ background: C.bg, minHeight: '100vh', paddingTop: 'var(--banner-h, 0px)' }}>
      <link
        href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;1,400;1,500;1,600&family=Source+Sans+3:wght@300;400;500;600&display=swap"
        rel="stylesheet"
      />
      <Nav />
      <PageHero />
      <TierComparison />
      <TierDetails />
      <FullProcess />
      <CTASection />
      <Footer />
    </div>
  );
}
